export interface TierConfig {
  threshold: string;
  thresholdType: 'lt' | 'gt' | 'range' | 'percentage';
  minValue?: number;
  maxValue?: number;
  scoreImpact: number;
}

export interface MiddleBestTiers {
  badLow: TierConfig; // Bad on the low end
  acceptable: TierConfig; // Acceptable buffer zone (low side)
  good: TierConfig; // Good in the middle
  acceptableHigh?: TierConfig; // Optional acceptable buffer zone (high side)
  badHigh: TierConfig; // Bad on the high end
}

export interface ScoringCriterion {
  id: string;
  name: string;
  description: string;
  rationale?: string; // Educational content explaining why this criterion matters
  category: 'relationships' | 'schedule' | 'float' | 'path';
  enabled: boolean;
  scoringType: 'tiered' | 'percentage' | 'middleBest';
  tiers?: {
    bad: TierConfig;
    acceptable: TierConfig;
    good: TierConfig;
  };
  middleBestTiers?: MiddleBestTiers; // For criteria where middle values are best
  percentageMultiplier?: number; // For percentage-based criteria
  daysThreshold?: number; // For criteria that measure against a day threshold (e.g., High Float, High Duration)
}

export interface GradeThreshold {
  grade: string;
  minScore: number;
  maxScore: number;
  color: string;
}

export const defaultGradeThresholds: GradeThreshold[] = [
  { grade: 'A', minScore: 90, maxScore: 100, color: 'success' },
  { grade: 'B', minScore: 80, maxScore: 89, color: 'primary' },
  { grade: 'C', minScore: 70, maxScore: 79, color: 'warning' },
  { grade: 'D', minScore: 60, maxScore: 69, color: 'orange' },
  { grade: 'F', minScore: 0, maxScore: 59, color: 'destructive' },
];

export const defaultCriteria: ScoringCriterion[] = [
  {
    id: 'relationship-ratio',
    name: 'Relationship Ratio',
    description: 'Ratio of relationships to activities in the schedule',
    rationale:
      'A healthy ratio of relationships to activities ensures proper task dependencies and logical flow. Too few relationships indicate missing logic links, which can lead to inaccurate critical path calculations and unreliable schedule forecasts.',
    category: 'relationships',
    enabled: true,
    scoringType: 'tiered',
    tiers: {
      bad: { threshold: '< 1.25', thresholdType: 'lt', maxValue: 1.25, scoreImpact: -5 },
      acceptable: {
        threshold: '1.25 to 1.5',
        thresholdType: 'range',
        minValue: 1.25,
        maxValue: 1.5,
        scoreImpact: -2.5,
      },
      good: { threshold: '> 1.5', thresholdType: 'gt', minValue: 1.5, scoreImpact: 0 },
    },
  },
  {
    id: 'finish-to-start',
    name: 'Finish to Start',
    description: 'Percentage of Finish-to-Start relationships',
    rationale:
      'Finish-to-Start is the most logical and intuitive relationship type, where a task begins only after its predecessor completes. Schedules should consist of at least 90% FS relationships to maintain clarity and ensure dependencies reflect realistic work sequences.',
    category: 'relationships',
    enabled: true,
    scoringType: 'tiered',
    tiers: {
      bad: { threshold: '< 80%', thresholdType: 'lt', maxValue: 80, scoreImpact: -2 },
      acceptable: {
        threshold: '80% - 90%',
        thresholdType: 'range',
        minValue: 80,
        maxValue: 90,
        scoreImpact: -1,
      },
      good: { threshold: '> 90%', thresholdType: 'gt', minValue: 90, scoreImpact: 0 },
    },
  },
  {
    id: 'start-to-start',
    name: 'Start to Start',
    description: 'Percentage of Start-to-Start relationships',
    rationale:
      'Start-to-Start relationships can be useful for parallel activities but should be limited. Excessive use may indicate improper task decomposition or unclear dependencies, reducing schedule transparency and making it harder to identify delays.',
    category: 'relationships',
    enabled: true,
    scoringType: 'tiered',
    tiers: {
      bad: { threshold: '> 10%', thresholdType: 'gt', minValue: 10, scoreImpact: -2 },
      acceptable: {
        threshold: '5% - 10%',
        thresholdType: 'range',
        minValue: 5,
        maxValue: 10,
        scoreImpact: -1,
      },
      good: { threshold: '< 5%', thresholdType: 'lt', maxValue: 5, scoreImpact: 0 },
    },
  },
  {
    id: 'finish-to-finish',
    name: 'Finish to Finish',
    description: 'Percentage of Finish-to-Finish relationships',
    rationale:
      'Finish-to-Finish relationships should be used sparingly as they can complicate schedule logic. When overused, they obscure the natural sequence of work and make it difficult to understand which tasks drive the critical path.',
    category: 'relationships',
    enabled: true,
    scoringType: 'tiered',
    tiers: {
      bad: { threshold: '> 10%', thresholdType: 'gt', minValue: 10, scoreImpact: -2 },
      acceptable: {
        threshold: '5% - 10%',
        thresholdType: 'range',
        minValue: 5,
        maxValue: 10,
        scoreImpact: -1,
      },
      good: { threshold: '< 5%', thresholdType: 'lt', maxValue: 5, scoreImpact: 0 },
    },
  },
  {
    id: 'missing-duration',
    name: 'Missing Duration',
    description:
      'Percentage of activities missing duration - that percentage is deducted from the score',
    rationale:
      'Tasks without durations cannot be scheduled accurately and prevent reliable timeline forecasting. Missing durations indicate incomplete planning and make it impossible to calculate the critical path or assess schedule performance realistically.',
    category: 'schedule',
    enabled: true,
    scoringType: 'percentage',
    percentageMultiplier: 1,
  },
  {
    id: 'missing-logic',
    name: 'Missing Logic',
    description:
      'Percentage of activities missing logic - that percentage is deducted from the score',
    rationale:
      "Tasks without predecessors or successors break the schedule's logical flow, preventing accurate critical path determination. Missing logic links mean the schedule cannot dynamically adjust to changes, undermining its reliability for anticipating delays and managing resources.",
    category: 'schedule',
    enabled: true,
    scoringType: 'percentage',
    percentageMultiplier: 1,
  },
  {
    id: 'number-of-lags',
    name: 'Number of Lags',
    description: 'Percentage of relationships with lag',
    rationale:
      'While lags can represent necessary waiting periods like curing concrete, excessive use distorts total float calculations and obscures task dependencies. Lags should be replaced with explicit tasks to maintain schedule clarity and ensure accurate critical path analysis.',
    category: 'schedule',
    enabled: true,
    scoringType: 'tiered',
    tiers: {
      bad: { threshold: '> 5%', thresholdType: 'gt', minValue: 5, scoreImpact: -3 },
      acceptable: {
        threshold: '2.5% - 5%',
        thresholdType: 'range',
        minValue: 2.5,
        maxValue: 5,
        scoreImpact: -1.5,
      },
      good: { threshold: '< 2.5%', thresholdType: 'lt', maxValue: 2.5, scoreImpact: 0 },
    },
  },
  {
    id: 'number-of-constraints',
    name: 'Number of Hard Constraints',
    description:
      'Percentage of activities with hard constraints (MFO, MSO, SNLT, FNLT) - that percentage is deducted from the score',
    rationale:
      "Hard constraints (Must Finish On, Must Start On, Start No Later Than, Finish No Later Than) override logical task dependencies, making schedules rigid and preventing natural flow. Soft constraints like 'Start On or After' or 'As Late As Possible' are acceptable and are not penalized.",
    category: 'schedule',
    enabled: true,
    scoringType: 'percentage',
    percentageMultiplier: 1,
  },
  {
    id: 'high-float',
    name: 'High Float',
    description: 'Percentage of activities with float greater than threshold',
    rationale:
      "Tasks with excessive float (over 44 days) often indicate missing logic or poorly defined dependencies. High float reduces schedule reliability because it suggests the schedule isn't truly logic-driven and may not accurately reflect how delays will propagate through the project.",
    category: 'float',
    enabled: true,
    scoringType: 'tiered',
    daysThreshold: 44,
    tiers: {
      bad: { threshold: '> 33%', thresholdType: 'gt', minValue: 33, scoreImpact: -2.5 },
      acceptable: {
        threshold: '20% - 33%',
        thresholdType: 'range',
        minValue: 20,
        maxValue: 33,
        scoreImpact: -1.5,
      },
      good: { threshold: '< 20%', thresholdType: 'lt', maxValue: 20, scoreImpact: 0 },
    },
  },
  {
    id: 'high-duration',
    name: 'High Duration',
    description: 'Percentage of activities with duration greater than threshold',
    rationale:
      "Tasks longer than 44 days create a 'pipeline effect' where delays are hidden until late in the task. Long-duration activities give false security that problems can be recovered later, but delays become exponentially harder to fix over time. Breaking them into smaller tasks enables earlier detection of issues.",
    category: 'schedule',
    enabled: true,
    scoringType: 'tiered',
    daysThreshold: 44,
    tiers: {
      bad: { threshold: '> 5%', thresholdType: 'gt', minValue: 5, scoreImpact: -5 },
      acceptable: {
        threshold: '2.5% - 5%',
        thresholdType: 'range',
        minValue: 2.5,
        maxValue: 5,
        scoreImpact: -2.5,
      },
      good: { threshold: '< 2.5%', thresholdType: 'lt', maxValue: 2.5, scoreImpact: 0 },
    },
  },
  {
    id: 'critical-path-percent',
    name: 'Critical Path %',
    description: 'Percentage of incomplete activities on the critical path (total float <= 0 days)',
    rationale:
      "The ideal critical path percentage is 10-20%. Below 10% suggests excessive float or missing logic, meaning the schedule won't accurately predict how changes impact completion. Above 30% means the schedule is too fragile with little flexibility, where any delay immediately impacts the overall timeline.",
    category: 'path',
    enabled: true,
    scoringType: 'middleBest',
    middleBestTiers: {
      badLow: { threshold: '< 5%', thresholdType: 'lt', maxValue: 5, scoreImpact: -5 },
      acceptable: {
        threshold: '5% - 10%',
        thresholdType: 'range',
        minValue: 5,
        maxValue: 10,
        scoreImpact: -2.5,
      },
      good: {
        threshold: '10% - 20%',
        thresholdType: 'range',
        minValue: 10,
        maxValue: 20,
        scoreImpact: 0,
      },
      acceptableHigh: {
        threshold: '20% - 30%',
        thresholdType: 'range',
        minValue: 20,
        maxValue: 30,
        scoreImpact: -2.5,
      },
      badHigh: { threshold: '> 30%', thresholdType: 'gt', minValue: 30, scoreImpact: -5 },
    },
  },
];

export const categoryLabels: Record<string, string> = {
  relationships: 'Relationship Types',
  schedule: 'Schedule Quality',
  float: 'Float Analysis',
  path: 'Critical Path',
};
